import { CreateClientDocument, GetClientsDocument, GetClientsBasicDocument } from "@/graphql/generated/graphql";
import { displayNotification } from "@/utils/notifications";
import { useMutation } from "@apollo/client/react";

interface UseCreateClientOptions {
  onSuccess?: (client?: { id: string; name: string } | null) => void;
  handleClose?: () => void;
  visible?: boolean;
}

export default function useCreateClient(options?: UseCreateClientOptions) {
  const { onSuccess, handleClose, visible = false } = options || {};

  return useMutation(CreateClientDocument, {
    update(cache, { data }) {
      const newClient = data?.createClient;
      if (!newClient) return;

      // 1. Update the Full Clients List
      const fullListData = cache.readQuery({
        query: GetClientsDocument,
        variables: { visible: true },
      });

      if (fullListData && visible) {
        cache.writeQuery({
          query: GetClientsDocument,
          variables: { visible: true },
          data: {
            getClients: [...(fullListData.getClients ?? []), newClient].sort((a, b) => a.name.localeCompare(b.name)),
          },
        });
      }

      // 2. Update the Basic Clients List
      const basicListData = cache.readQuery({
        query: GetClientsBasicDocument,
      });

      if (basicListData) {
        cache.writeQuery({
          query: GetClientsBasicDocument,
          data: {
            getClients: [
              ...(basicListData.getClients ?? []),
              {
                id: newClient.id,
                name: newClient.name,
                __typename: newClient.__typename
              },
            ].sort((a, b) => a.name.localeCompare(b.name)),
          },
        });
      }
    },
    onCompleted: (data) => {
      displayNotification({ type: "success", text: "Client Created Successfully" });
      onSuccess?.(data?.createClient);
      handleClose?.();
    },
    onError: (error) => {
      displayNotification({ type: "error", text: error.message });
    },
  });
}
